import React, { useEffect, useState } from "react";
import { $Avatar, $Tooltip } from "../antd";
import { UserOutlined } from "us.icons";
import { UserService } from "us.common/services";
import { AvatarSize } from "antd/lib/avatar/SizeContext";

type ProfileImageProps = {
  userId: string;
  size?: AvatarSize | undefined;
  className?: string;
  enableTooltip?: boolean;
  tooltipTitle?: string;
};

/**
 * ProfileImage component that displays a user's profile image.
 *
 * @component
 * @param {ProfileImageProps} props - The properties for the ProfileImage component.
 * @param {string} props.userId - The ID of the user whose profile image is to be displayed.
 * @param {string} [props.className] - Optional additional class name for styling the avatar.
 *
 * @returns {JSX.Element} The rendered ProfileImage component.
 *
 * @example
 * <ProfileImage userId="12345" className="custom-class" />
 *
 * @remarks
 * This component fetches the user's profile image based on the provided userId.
 * If the userId is not provided or an error occurs during the fetch, a default icon is displayed.
 */
const ProfileImage: React.FC<ProfileImageProps> = ({
  userId,
  size = "default",
  className,
  enableTooltip = false,
  tooltipTitle = "",
}: ProfileImageProps): JSX.Element => {
  const [imageSrc, setImageSrc] = useState<string | null>(null);

  useEffect(() => {
    if (!userId) {
      setImageSrc(null);
      return;
    }

    const fetchProfileImage = async () => {
      UserService.userProfileImage
        .getById({ userId })
        .then(async (response) => {
          setImageSrc(response);
        })
        .catch((error) => {
          console.error("Error fetching profile image:", error);
          setImageSrc(null);
        });
    };

    fetchProfileImage();
  }, [userId]);

  return enableTooltip ? (
    <$Tooltip title={tooltipTitle}>
      <$Avatar
        src={imageSrc}
        alt="User Profile"
        size={size}
        className={className}
        icon={<UserOutlined />}
      />
    </$Tooltip>
  ) : (
    <$Avatar
      src={imageSrc}
      alt="User Profile"
      size={size}
      className={className}
      icon={<UserOutlined />}
    />
  );
};

export default ProfileImage;
